import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import moment from "moment";
import { Button } from "../../components/ui/button";
import Common from "../../helper/common";
import AdvanceTable from "../../components/advanceTable";
import { useNavigate } from "react-router-dom";
import { useQueryParams } from "../../contexts/queryParamsContext";
import { AppSelect, AppSearchSelect } from "../../components/appComponents";
import {
  PromoCodeDiscountTypeEnum,
  respEnum,
  ViewDateTimeFormat,
  SearchTypeEnum,
} from "../../constants/appConstant";

const BankDiscounts = () => {
  const [data, setData] = useState({});

  // Filter states
  const [bankId, setBankId] = useState(null);
  const [bankCardTypeId, setBankCardTypeId] = useState(null);
  const [bankCardCategoryId, setBankCardCategoryId] = useState(null);
  const [isActive, setIsActive] = useState(null);
  const [discountType, setDiscountType] = useState(null);

  // Dropdown lists
  const [bankList, setBankList] = useState([]);
  const [bankCardTypeList, setBankCardTypeList] = useState([]);
  const [bankCardCategoryList, setBankCardCategoryList] = useState([]);

  const navigate = useNavigate();
  const { setId } = useQueryParams();

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    getBanks();
    getBankCardTypes();
    getBankCardCategories();
  }, []);

  useEffect(() => {
    getData(1);
  }, [bankId, bankCardTypeId, bankCardCategoryId, isActive, discountType]);

  const getBanks = async () => {
    const response = await Common.ApiService.getInstance().request("GetBanks", {
      isactive: true,
    });
    if (response?.status === respEnum.Success) {
      const banks = [
        { id: null, name: "All Banks" },
        ...(response?.data ?? []),
      ];
      setBankList(banks);
    }
  };

  const getBankCardTypes = async () => {
    const response = await Common.ApiService.getInstance().request(
      "GetBankCardType",
      { isactive: true }
    );
    if (response?.status === respEnum.Success) {
      const cardTypes = [
        { id: null, name: "All Card Types" },
        ...(response?.data ?? []),
      ];
      setBankCardTypeList(cardTypes);
    }
  };

  const getBankCardCategories = async () => {
    const response = await Common.ApiService.getInstance().request(
      "GetBankCardCategories",
      { isactive: true }
    );
    if (response?.status === respEnum.Success) {
      const categories = [
        { id: null, name: "All Categories" },
        ...(response?.data ?? []),
      ];
      setBankCardCategoryList(categories);
    }
  };

  const resetFilters = () => {
    setBankId(null);
    setBankCardTypeId(null);
    setBankCardCategoryId(null);
    setIsActive(null);
    setDiscountType(null);
  };

  const getData = async (
    pageNumber,
    searchTerm = "",
    recordsToDisplay = null
  ) => {
    const response = await Common.ApiService.getInstance().request(
      "GetClubBankDiscounts",
      {
        pageNumber: pageNumber,
        q: searchTerm,
        recordsToDisplay,
        bankid: bankId,
        bankcardtypeid: bankCardTypeId,
        bankcardcategoryid: bankCardCategoryId,
        isactive: isActive,
        discounttype: discountType,
      },
      "Post"
    );
    if (response?.data) {
      let obj = {
        ...response.data[0],
        data: response.data[0].data.map((item) => ({
          ...item,
          validfrom: item.validfrom
            ? `${moment(item.validfrom).format(ViewDateTimeFormat)}`
            : "-",
          validtill: item.validtill
            ? `${moment(item.validtill).format(ViewDateTimeFormat)}`
            : "-",
          discountValue:
            item.discountType == PromoCodeDiscountTypeEnum.Percentage
              ? `${item.discountValue} %`
              : item.discountValue,
          clubbearingpercent:item.clubbearingpercent? `${item.clubbearingpercent} %` :"-",
          bankbearingpercent:item.bankbearingpercent? `${item.bankbearingpercent} %` :"-",
          maxDiscount: item.maxDiscount ?? "-",
          minOrderAmount: item.minOrderAmount ?? "-",
          transactionallowedperday: item.transactionallowedperday ?? "-",
          discountTypeId: item.discountType,
          discountType: Common.Utility.getKeyByValue(
            PromoCodeDiscountTypeEnum,
            item.discountType
          ),
          isdisplayonapp: item.isdisplayonapp ? "Yes" : "No",
        })),
      };
      setData(obj);
    }
  };

  const _handleDelete = async (obj) => {
    debugger;
    const result = await Common.SweetAlert.confirmation(
      "Are you sure you want to delete this bank discount?",
      "This action cannot be undone."
    );

    if (result) {
      let resp = await Common.ApiService.getInstance().request(
        `DeleteClubBankDiscount?id=${obj.id}`,
        {},
        "Delete"
      );
      if (resp.status == respEnum.Success) {
        Common.SweetAlert.alert(resp.message, "success");
        setTimeout(() => {
          getData(1);
        }, 300);
      }
    }
  };

  const _thead = [
    { title: "Id", type: "number", isVisible: false, key: "id" },
    { title: "Name", type: "text", isVisible: true, key: "name" },
    { title: "Valid From", type: "text", isVisible: true, key: "validfrom" },
    { title: "Valid Till", type: "text", isVisible: true, key: "validtill" },
    { title: "Bin Number", type: "text", isVisible: true, key: "bin" },
    {
      title: "Discount Type",
      type: "text",
      isVisible: true,
      key: "discountType",
    },
    {
      title: "Discount Value",
      type: "text",
      isVisible: true,
      key: "discountValue",
    },
    {
      title: "Min Order",
      type: "text",
      isVisible: true,
      key: "minOrderAmount",
    },
    {
      title: "Max Discount",
      type: "text",
      isVisible: true,
      key: "maxDiscount",
    },

    {
      title: "Daily Limit",
      type: "text",
      isVisible: true,
      key: "transactionallowedperday",
    },
    {
      title: "Club Percentage",
      type: "number",
      isVisible: true,
      key: "clubbearingpercent",
    },
    {
     title: "Bank Percentage",
     type: "number",
     isVisible: true,
     key: "bankbearingpercent",
   },
    {
      title: "Display on App",
      type: "text",
      isVisible: true,
      key: "isdisplayonapp",
    },
    {
      title: "Is Active",
      type: "toggle",
      isVisible: false,
      key: "isactive",
      // onChanged: (item) => _handleOnChange(item),
    },
  ];

  const actions = [
    {
      type: "edit",
      onClick: (val) => {
        setId(val.id);
        navigate("/app/addbankdiscount");
      },
    },
    {
      type: "delete",
      onClick: (val) => _handleDelete(val),
    },
  ];

  const _addBtn = () => (
    <Button onClick={() => navigate("/app/addbankdiscount")}>
      <span className="text-xl mr-1">
        <Icon icon="icon-park-outline:plus" />
      </span>
      <span className="hidden sm:inline">Add Bank Discount</span>
    </Button>
  );

  return (
    <>
      <div className="md:grid md:grid-cols-5 grid grid-cols-2 gap-x-2 ">
        <AppSearchSelect
          value={bankId}
          onChange={setBankId}
          placeholder="All Banks"
          label="Bank"
          data={bankList}
          className="mb-3 col-span-1"
        />

        <AppSearchSelect
          value={bankCardTypeId}
          onChange={setBankCardTypeId}
          placeholder="All Card Types"
          label="Card Type"
          data={bankCardTypeList}
          className="mb-3 col-span-1"
        />

        <AppSearchSelect
          value={bankCardCategoryId}
          onChange={setBankCardCategoryId}
          placeholder="All Categories"
          label="Card Category"
          data={bankCardCategoryList}
          className="mb-3 col-span-1"
        />

        <AppSelect
          value={isActive}
          onChange={setIsActive}
          placeholder="Select Status"
          label="Status"
          data={[
            { id: null, name: "All" },
            { id: true, name: "Active" },
            { id: false, name: "Inactive" },
          ]}
          className="mb-3 col-span-1"
        />

        <AppSelect
          value={discountType}
          onChange={setDiscountType}
          placeholder="Select Discount Type"
          label="Discount Type"
          data={[
            { id: null, name: "All" },
            ...Common.Utility.enumToArray(PromoCodeDiscountTypeEnum),
          ]}
          className="mb-3 col-span-1"
        />
      </div>

      <div className="md:grid md:grid-cols-5 grid grid-cols-2 gap-x-2 mb-3">
        <Button className="h-8 w-fit" onClick={resetFilters}>
          Reset Filters
        </Button>
      </div>

      {data?.data && (
        <AdvanceTable
          title={"Bank Discounts"}
          columns={_thead.filter((item) => item.isVisible)}
          data={data}
          getData={getData}
          addButton={_addBtn}
          showPagination
          showAddButton
          actionButtons={actions}
          search={SearchTypeEnum.Server_Search}
          searchInputClassName="custom-search-mobile "
        />
      )}
    </>
  );
};

export default BankDiscounts;
